import { useEffect, useState, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { API_URL } from '../api/client';
import {
  subscribeToAuthChanges,
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  signOut as firebaseSignOut,
  resetPassword,
  getIdToken,
  type User as FirebaseUser,
} from '../lib/firebase';
import { AuthContext, type UserProfile } from './useAuth';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserProfile = async (token: string, signal?: AbortSignal) => {
    try {
      const response = await fetch(`${API_URL}/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      if (response.ok) {
        const profile = await response.json();
        setUser(profile);
      } else {
        console.error('Failed to fetch user profile:', response.status);
        setUser(null);
      }
    } catch (err) {
      // Ignore abort errors - they're expected on cleanup
      if (err instanceof Error && err.name === 'AbortError') return;
      Sentry.captureException(err, { tags: { component: 'AuthContext' } });
      console.error('Error fetching user profile:', err);
      setUser(null);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const unsubscribe = subscribeToAuthChanges(async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        const token = await fbUser.getIdToken();
        await fetchUserProfile(token, controller.signal);
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return () => {
      controller.abort();
      unsubscribe();
    };
  }, []);

  const loginWithGoogle = async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to sign in with Google';
      setError(message);
      throw err;
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    setError(null);
    try {
      await signInWithEmail(email, password);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setError(message);
      throw err;
    }
  };

  const signUpWithEmailPassword = async (email: string, password: string) => {
    setError(null);
    try {
      await signUpWithEmail(email, password);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create account';
      setError(message);
      throw err;
    }
  };

  const logout = async () => {
    setError(null);
    try {
      await firebaseSignOut();
      setUser(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign out';
      setError(message);
      throw err;
    }
  };

  const sendPasswordReset = async (email: string) => {
    setError(null);
    try {
      await resetPassword(email);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to send password reset email';
      setError(message);
      throw err;
    }
  };

  const getToken = async () => {
    return getIdToken();
  };

  const refreshProfile = async () => {
    const token = await getToken();
    if (token) {
      await fetchUserProfile(token);
    }
  };

  const isAdmin = user?.role === 'ADMIN';

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        user,
        loading,
        error,
        loginWithGoogle,
        loginWithEmail,
        signUpWithEmailPassword,
        logout,
        sendPasswordReset,
        getToken,
        refreshProfile,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
